import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const WishlistContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"}/api`;

export const WishlistProvider = ({ children }) => {
  const { user, accessToken, loading: authLoading } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchWishlist = useCallback(async () => {
    if (authLoading) return;
    if (!user || !accessToken) {
      setWishlist([]);
      setError(false);
      return;
    }
    try {
      setLoading(true);
      setError(false);
      const res = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const validItems = (res.data || []).filter((item) => item && item.games);
      setWishlist(validItems);
      setError(false);
    } catch (err) {
      console.error("Fetch wishlist error:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user, accessToken, authLoading]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "cg39_wishlist_sync") {
        fetchWishlist();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [fetchWishlist]);

  const toggleWishlist = async (gameId, gameData = null) => {
    if (!user || !accessToken) {
      toast.error("Please login first");
      return false;
    }
    const exists = wishlist.some((item) => String(item.game_id || item.games?.id) === String(gameId));
    
    // OPTIMISTIC UPDATE: Update state immediately with valid game object to prevent render crashes
    if (exists) {
      setWishlist((prev) => prev.filter((item) => String(item.game_id || item.games?.id) !== String(gameId)));
    } else {
      const optimisticItem = {
        game_id: gameId,
        id: `temp-${Date.now()}`,
        games: gameData || { id: gameId, title: "Saved Game", price: 0 }
      };
      setWishlist((prev) => [...prev, optimisticItem]);
    }
    localStorage.setItem("cg39_wishlist_sync", Date.now().toString());

    try {
      if (exists) {
        await axios.delete(`${API}/wishlist/${gameId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        return false;
      } else {
        await axios.post(
          `${API}/wishlist/${gameId}`,
          {},
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        // Sync in background without re-triggering loading spinners
        const res = await axios.get(`${API}/wishlist`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const validItems = (res.data || []).filter((item) => item && item.games);
        setWishlist(validItems);
        return true;
      }
    } catch (err) {
      console.error("Toggle wishlist error:", err);
      // Rollback optimistic update on failure
      fetchWishlist();
      toast.error("Wishlist action failed");
      return exists;
    }
  };

  const isGameInWishlist = (gameId) => {
    return wishlist.some((item) => String(item.game_id || item.games?.id) === String(gameId));
  };

  const wishlistCount = wishlist.length;

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        wishlistCount,
        loading: loading || (authLoading && !user),
        error,
        toggleWishlist,
        isGameInWishlist,
        refreshWishlist: fetchWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => useContext(WishlistContext);
